// iot-admin/client/src/auth/AuthGate.js
//
// Client-side access gate: the IoT Admin console renders ONLY for authenticated
// super-admins. Everyone else gets a clean "access restricted" screen instead
// of the app shell (previously they could load the chrome and just hit 403s).
//
// This is purely front-end. It does NOT touch any backend / device / service /
// internal API — those keep their own auth (super-admin per-route for admin
// data; bootstrap-cert / device-token / API-key for device & service callers).

import React, { useEffect, useState } from 'react';
import { authClient } from './authClient';
import './AuthGate.css';

// Dev bypass: the CRA dev server (npm run client) has no login.galigali.in
// session, so the gate would otherwise redirect away and the console never
// renders locally. This gate is purely cosmetic — every backend route keeps its
// own super-admin auth (and the local server runs with MOCK_AUTH) — so skipping
// it in development is safe. Compiled OUT of production builds (NODE_ENV=production).
// Set REACT_APP_TEST_AUTH_GATE=true to exercise the gate UI in dev.
const DEV_BYPASS =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_TEST_AUTH_GATE !== 'true';

const readAuth = () => ({
  authed: authClient.isAuthenticated(),
  user: authClient.getUser(),
});

export default function AuthGate({ children }) {
  const [{ authed, user }, setAuth] = useState(readAuth);

  useEffect(() => {
    const sync = () => setAuth(readAuth());
    sync(); // re-read once mounted (tokens may have just been stored)
    const unsub = authClient.onAuthChange(sync);
    return () => { if (typeof unsub === 'function') unsub(); };
  }, []);

  if (DEV_BYPASS) {
    return children;
  }

  // Not authenticated → the shared auth client redirects to login.galigali.in.
  if (!authed) {
    return (
      <div className="ag-screen">
        <div className="ag-card">
          <div className="ag-spin" />
          <h1>Signing you in…</h1>
          <p className="ag-sub">Redirecting to login.galigali.in</p>
        </div>
      </div>
    );
  }

  // Authenticated but not a super-admin → access denied.
  if (!user?.isSuperAdmin) {
    return (
      <div className="ag-screen">
        <div className="ag-card">
          <div className="ag-lock">🔒</div>
          <h1>Access restricted</h1>
          <p className="ag-sub">
            The IoT Admin console is available to <strong>GaliGali super-admins</strong> only.
          </p>
          {(user?.email || user?.name) && (
            <p className="ag-who">Signed in as <strong>{user.email || user.name}</strong></p>
          )}
          <p className="ag-hint">
            If you believe you should have access, please contact a platform administrator.
          </p>
          <div className="ag-actions">
            <button className="ag-btn" onClick={() => authClient.logout()}>Sign out</button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
